/**
 * The Data refinery's staff actions: ask Task generation to plan tests
 * again (POST /api/proven-tasks/regenerate), and start it now when the job
 * can (the staff Jobs page's run route), and keep proven tasks out of the
 * dataset builds or let them back in (PUT /api/proven-tasks/exclusions).
 *
 * The api decides what may happen and says why when it refuses, so every
 * refusal is its detail in a toast. A refusal also means the page read an
 * older state (a stale exclusions revision, a test the night no longer
 * scores), so the tree is read again whatever the answer.
 */

import { type QueryClient, type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { type ApiRequestError, getApiErrorDetail } from '@/api/client'
import {
  getListTeamJobsApiInternalTeamsTeamIdJobsGetQueryKey,
  runTeamJobApiInternalTeamsTeamIdJobsJobSlugRunPost,
} from '@/api/generated/internal/internal'
import {
  getReadRefineryTreeApiProvenTasksTreeGetQueryKey,
  regenerateTestsApiProvenTasksRegeneratePost,
  type RegenerateTestsApiProvenTasksRegeneratePostMutationVariables,
  type SaveExclusionsApiProvenTasksExclusionsPutMutationVariables,
  useSaveExclusionsApiProvenTasksExclusionsPut,
} from '@/api/generated/proven-tasks/proven-tasks'
import { type GenerateResult, REFINERY_COPY } from '@/components/refinery/rows'

/** The staff Jobs row Generate starts (services/team_jobs.py). */
export const TASK_GENERATION_JOB = 'task-generation'

interface ActionCallbacks {
  onError: (error: ApiRequestError) => void
  onSettled: () => void
}

/** Every refinery tree the cache holds: no params, so the key prefix covers every benchmark. */
function readTreesAgain(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: getReadRefineryTreeApiProvenTasksTreeGetQueryKey() })
}

/**
 * What a regenerate or an exclusions save does to the page: a refusal is
 * the api's detail, else `failed`, and every tree is read again. Exported
 * for its test.
 */
export function treeActionCallbacks(queryClient: QueryClient, failed: string): ActionCallbacks {
  return {
    onError: (error) => {
      toast.error(getApiErrorDetail(error) ?? failed)
    },
    onSettled: () => {
      readTreesAgain(queryClient)
    },
  }
}

/** Exclude or include proven tasks, naming the exclusions revision the tree was read at. */
export function useSetExcluded(): UseMutationResult<
  unknown,
  ApiRequestError,
  SaveExclusionsApiProvenTasksExclusionsPutMutationVariables
> {
  const queryClient = useQueryClient()
  return useSaveExclusionsApiProvenTasksExclusionsPut({
    mutation: treeActionCallbacks(queryClient, REFINERY_COPY.failed.exclusions),
  })
}

/** What Generate sends: the request (`params.benchmark` is the tree's), and whether the job can start. */
export interface GenerateVariables extends RegenerateTestsApiProvenTasksRegeneratePostMutationVariables {
  start: boolean
}

/** Start Task generation for the team after its request landed, saying a refusal as a result. */
async function startAfterSave(teamId: string): Promise<GenerateResult> {
  try {
    const started = await runTeamJobApiInternalTeamsTeamIdJobsJobSlugRunPost(teamId, TASK_GENERATION_JOB)
    // orvalFetch throws on every non-2xx, so only the type holds another status.
    return started.status === 200 ? { kind: 'started' } : { kind: 'refused', reason: null }
  } catch (error) {
    return { kind: 'refused', reason: getApiErrorDetail(error) ?? null }
  }
}

/**
 * Generate's calls, in order: the request, then the run when the job can
 * start. A refused request throws and starts nothing; a refused run after
 * it is a result, since the request is saved by then. Exported for its test.
 */
export async function saveThenStart(
  teamId: string,
  { data, params, start }: GenerateVariables,
): Promise<GenerateResult> {
  await regenerateTestsApiProvenTasksRegeneratePost(data, params)
  return start ? startAfterSave(teamId) : { kind: 'saved' }
}

/**
 * What Generate does to the page: a refused save is the api's detail, and
 * every tree and the team's jobs are read again either way. The page says
 * the result itself, in the line under the bar. Exported for its test.
 */
export function generateCallbacks(queryClient: QueryClient, teamId: string): ActionCallbacks {
  return {
    ...treeActionCallbacks(queryClient, REFINERY_COPY.failed.regenerate),
    onSettled: () => {
      readTreesAgain(queryClient)
      void queryClient.invalidateQueries({
        queryKey: getListTeamJobsApiInternalTeamsTeamIdJobsGetQueryKey(teamId),
      })
    },
  }
}

/** Ask Task generation to plan the tests again, and start it now when `start` says the job can. */
export function useGenerateTasks(teamId: string): UseMutationResult<GenerateResult, ApiRequestError, GenerateVariables> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (variables) => saveThenStart(teamId, variables),
    ...generateCallbacks(queryClient, teamId),
  })
}
